/* eslint-disable */
import * as Sentry from '@sentry/angular';
import { environment } from '../../environment/environment';
import { Capacitor } from '@capacitor/core';
import { setSentryContext, setSentryTag } from './sentry.util';
import { AppVersion } from '@core';

const SENTRY_DSN = 'https://92bb45371de7e112a2a83cbf6bc2948d@o4507171930898433.ingest.de.sentry.io/4507172141989968';

export const initSentry = (): void => {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  const isLocalDev = !isNative && location.hostname === 'localhost';

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: environment.environment,
    release: `orlikfy-web@${environment.packageName}`,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({
        console: true,
        fetch: true,
        history: true,
        xhr: true
      })
    ],

    tracesSampleRate: 0.6,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.staging\.orlikfy\.pl\/.*/,
      /^https:\/\/api\.prod\.orlikfy\.pl\/.*/
    ],

    ignoreErrors: [
      // Błędy przeglądarki/layoutu
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',

      // Błędy ładowania chunków (słabe połączenie, odświeżenie podczas deployu)
      /ChunkLoadError/i,
      /Loading chunk/i,

      // Błędy rozszerzeń przeglądarki
      /extension\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,

      // Anulowanie natywnego dialogu udostępniania
      'Share canceled',

      // Google One Tap / Credential Manager - problemy po stronie urządzenia
      /failure response from one tap.*User disabled the feature/i,
      'getCredentialAsync no provider dependencies found',
      "Your device doesn't support credential manager",
      'No credentials available',

      // Błędy sieciowe (słabe połączenie użytkownika)
      /timeout of \d+ms exceeded/i,

      // Anulowanie logowania Google przez użytkownika
      /Cancelled by user/i,

      // Anulowanie aktywności logowania na Androidzie
      /activity is cancelled by the user/i,

      // Ionic - overlay już nie istnieje (timing przy nawigacji)
      'overlay does not exist',

      // Błędy wbudowanej przeglądarki Facebooka (in-app browser)
      /window\.webkit\.messageHandlers.*postMessage/i,

      // Ionicons/Stencil.js adoptedStyleSheets na Chrome < 99 (FrozenArray)
      'Cannot add property 0, object is not extensible'

      // Błędy walidacji Firebase Auth — obsługiwane przez getFirebaseAuthErrorMessage
      // 'The email address is badly formatted.',
      // 'The password is invalid or the user does not have a password.',
      // 'The email address is already in use by another account.',
      // 'The supplied auth credential is incorrect, malformed or has expired.',
    ],

    beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }
      return breadcrumb;
    },

    beforeSend(event: Sentry.ErrorEvent) {
      const blockLocalEvents = true;
      if (blockLocalEvents && isLocalDev) {
        return null;
      }

      // Filtruj błędy HTTP ze statusem 0 (anulowane requesty, utrata sieci, przejście do tła na iOS)
      const errorMessage = event.exception?.values?.[0]?.value?.toLowerCase() ?? '';
      if (/http failure response for .+: 0 /.test(errorMessage)) {
        return null;
      }
      if (
        errorMessage.includes('user cancelled') || // Google Auth canceled
        errorMessage.includes('user canceled') || // Google Auth canceled
        errorMessage.includes('authorizationerror error 1000') || // Apple Sign-In cancelled
        errorMessage.includes('authorizationerror error 1001') // Apple Sign-In cancelled (unknown)
      ) {
        return null;
      }

      return event;
    }
  });

  setSentryContext('app', {
    version: AppVersion,
    platform,
    environment: environment.environment,
    packageName: environment.packageName
  });

  setSentryTag('platform', platform);
  setSentryTag('environment', environment.environment);
  setSentryTag('appVersion', AppVersion);
};
